import * as React from 'react';
import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';

import type { VariantProps } from 'class-variance-authority';

const alertVariants = cva(
	'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
	{
		variants: {
			variant: {
				default: 'bg-background text-foreground',
				destructive: 'border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive',
				// Notices. The tint is the state's own subtle token, never brand: purple is not a
				// surface colour, and about thirty banners were painting `bg-brand-subtle` boxes.
				info: 'border-info-border bg-info-subtle text-foreground [&>svg]:text-info',
				success: 'border-success-border bg-success-subtle text-foreground [&>svg]:text-success',
				warning: 'border-warning-border bg-warning-subtle text-foreground [&>svg]:text-warning',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	},
);

type AlertProps = React.HTMLAttributes<HTMLDivElement> &
	VariantProps<typeof alertVariants> & {
		/**
		 * The one thing to do about it (a link button, a small button). Sits to the right of the
		 * text, level with the description's last line, when the alert is wide enough, and wraps
		 * under it, aligned with the title, when not -
		 * decided by the alert's own width, so it also works inside a narrow card.
		 */
		action?: React.ReactNode;
	};

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(({ className, variant, action, children, ...props }, ref) => {
	const role = variant === 'destructive' ? 'alert' : 'note';
	if (!action) {
		return (
			<div ref={ref} role={role} className={cn(alertVariants({ variant }), className)} {...props}>
				{children}
			</div>
		);
	}
	// With an action the icon takes its own column, so text and action can wrap beside it
	// and stay aligned with the title.
	const parts = React.Children.toArray(children);
	const iconIndex = parts.findIndex((c) => React.isValidElement(c) && c.type !== AlertTitle && c.type !== AlertDescription);
	const icon = iconIndex === 0 ? parts[0] : null;
	const text = icon ? parts.slice(1) : parts;
	// The icon sits on the first line of text: a title's line is the icon's own 16px, a
	// description's is 20px, so without a title it moves down to that line's middle.
	const hasTitle = text.some((c) => React.isValidElement(c) && c.type === AlertTitle);
	return (
		<div ref={ref} role={role} className={cn(
				alertVariants({ variant }),
				// Undo the absolute icon and the text indent the plain layout uses.
				'flex items-start gap-3 [&>svg+div]:translate-y-0 [&>svg]:static [&>svg]:shrink-0 [&>svg~*]:pl-0',
				!hasTitle && '[&>svg]:translate-y-0.5',
				className,
			)} {...props}>
			{icon}
			<div className="flex min-w-0 flex-1 flex-wrap items-end gap-x-6 gap-y-2">
				<div className="min-w-0 grow basis-64">{text}</div>
				{/* One text line high, so the button (taller than a line of text) lines up with
				    the description's last line instead of pushing the text down to its own bottom. */}
				<div className="flex h-5 shrink-0 items-center">{action}</div>
			</div>
		</div>
	);
});
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
	({ className, ...props }, ref) => (
		<h5 ref={ref} className={cn('mb-1 font-medium leading-none tracking-tight', className)} {...props} />
	),
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
	({ className, ...props }, ref) => (
		<div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
	),
);
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
